import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { read } from "hooks/useSession.ts";
import { models } from "types/api/viewModels.ts";
import { getCabCubeCountsFromDraft } from "utils/order";

const truck = read("_truck");

export interface TruckSlice {
	truck?: models["TruckViewModel"];
	selectedAccount?: models["CustomerAccountViewModel"];
}

export const initialState: TruckSlice = truck || {
	truck: undefined,
	selectedAccount: undefined
};

const calculateCubeAndCabCount = (stops: models["TruckStopViewModel"][] = []) => {
	let runningCubeCount = 0;
	let runningCabinetCount = 0;

	for (let stop of stops) {
		for (let draftOrder of stop.draftOrders ?? []) {
			const { cabinetCount, cubeCount } = getCabCubeCountsFromDraft(draftOrder);
			runningCubeCount += cubeCount ?? 0;
			runningCabinetCount += cabinetCount ?? 0;
		}
	}
	return { cubeCount: runningCubeCount, cabinetCount: runningCabinetCount };
};

const truckSlice = createSlice({
	name: "trucks",
	initialState,
	reducers: {
		updateTruck: (state, action: PayloadAction<models["TruckViewModel"]>) => {
			state.truck = action.payload;
			if (state.truck.stops) {
				state.truck = { ...state.truck, ...calculateCubeAndCabCount(state.truck?.stops) };
			}
		},
		clearTruck: (state) => {
			state.truck = undefined;
			state.selectedAccount = undefined;
		},
		updateRequestDate: (state, action) => {
			state.truck = {
				...state.truck,
				requestedDeliveryDate: action.payload
			};
		},
		updateSelectedAccount: (state, action) => {
			state.selectedAccount = action.payload;
		},
		addStop: (state, action: PayloadAction<models["TruckStopViewModel"]>) => {
			state.truck?.stops?.push(action.payload);
		},
		updateStop: (
			state,
			{ payload: { index, stop } }: PayloadAction<{ index: number; stop: models["TruckStopViewModel"] }>
		) => {
			if (state.truck?.stops?.[index]) {
				state.truck.stops[index] = {
					...state.truck.stops[index],
					...stop
				};
			}
		},
		removeStop: (state, { payload: { index } }: PayloadAction<{ index: number }>) => {
			if (state.truck?.stops?.[index]) {
				state.truck.stops = [
					...state.truck.stops.slice(0, index),
					...state.truck.stops.slice(index + 1, state.truck.stops.length)
				];

				state.truck = { ...state.truck, ...calculateCubeAndCabCount(state.truck.stops) };
			}
		},
		removeDraftOrder: (
			state,
			{
				payload: { index, draftOrderId }
			}: PayloadAction<{
				index: number | undefined;
				draftOrderId: string | null | undefined;
			}>
		) => {
			if ((index || index === 0) && state.truck?.stops?.[index]) {
				const stop = state.truck.stops[index];
				stop.draftOrders = stop.draftOrders?.filter(
					(existingDraft) => existingDraft.draftOrderId !== draftOrderId
				);
				stop.draftOrderIds = stop.draftOrderIds?.filter((id) => id !== draftOrderId);
				const { cubeCount, cabinetCount } = calculateCubeAndCabCount(state.truck.stops);
				state.truck.cubeCount = cubeCount;
				state.truck.cabinetCount = cabinetCount;
			}
		},
		addDraftOrderToStop: (state, action: { payload: { index: number; draft: models["DraftOrderViewModel"] } }) => {
			if (state.truck?.stops?.[action.payload.index]) {
				state.truck.stops[action.payload.index]!.draftOrders = [
					...(state.truck.stops[action.payload.index]?.draftOrders ?? []),
					action.payload.draft
				];
				state.truck.stops[action.payload.index]!.draftOrderIds = [
					...(state.truck.stops[action.payload.index]?.draftOrderIds ?? []),
					action.payload.draft.draftOrderId!
				];

				state.truck = { ...state.truck, ...calculateCubeAndCabCount(state.truck?.stops) };
			}
		}
	}
});

export const truckActions = truckSlice.actions;
export default truckSlice.reducer;
